import React, { useState } from 'react';
import { X, MessageSquare, Bug, Star, Palette, Lightbulb, MessageCircle } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useAuth } from '@/contexts/auth-context';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentRoute?: string;
}

const feedbackTypes = [
  { value: 'bug', label: 'Bug Report', icon: Bug, description: 'Something isn\'t working correctly' },
  { value: 'answer_quality', label: 'Answer Quality', icon: Star, description: 'AI response quality or accuracy' },
  { value: 'ux_ui', label: 'UX / UI', icon: Palette, description: 'Design, usability, or interface issues' },
  { value: 'feature_request', label: 'Feature Request', icon: Lightbulb, description: 'Suggest new features or improvements' },
  { value: 'general', label: 'General Feedback', icon: MessageCircle, description: 'Other thoughts or suggestions' }
];

export default function FeedbackModal({ isOpen, onClose, currentRoute }: FeedbackModalProps) {
  const { user } = useAuth();
  const [feedbackType, setFeedbackType] = useState('');
  const [shortSummary, setShortSummary] = useState('');
  const [detailedFeedback, setDetailedFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedbackType || !shortSummary.trim()) return;

    setIsSubmitting(true);
    try {
      await apiRequest('POST', '/api/feedback', {
        feedback_type: feedbackType,
        short_summary: shortSummary.trim(),
        detailed_feedback: detailedFeedback.trim() || null,
        route: currentRoute || window.location.pathname,
        session_id: sessionStorage.getItem('veritus_session_id') || null,
        user_agent: navigator.userAgent
      });

      // Show success state
      setShowSuccess(true);
      setTimeout(() => {
        onClose();
        setShowSuccess(false);
        setFeedbackType('');
        setShortSummary('');
        setDetailedFeedback('');
      }, 2000);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-background border border-border rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
              <span className="text-white text-lg">✓</span>
            </div>
          </div>
          <h3 className="text-xl font-semibold mb-2">Thanks for your feedback!</h3>
          <p className="text-muted-foreground">Your feedback helps shape Veritus.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-background border border-border rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">Give Feedback</h2>
              <p className="text-sm text-muted-foreground">Help us improve Veritus</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Close feedback modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Feedback Type */}
          <div>
            <label className="block text-sm font-medium mb-3">What type of feedback is this? *</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {feedbackTypes.map((type) => {
                const IconComponent = type.icon;
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFeedbackType(type.value)}
                    className={`p-4 rounded-xl border text-left transition-all hover:border-primary/50 ${
                      feedbackType === type.value
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        feedbackType === type.value ? 'bg-primary/20' : 'bg-muted'
                      }`}>
                        <IconComponent className={`w-4 h-4 ${
                          feedbackType === type.value ? 'text-primary' : 'text-muted-foreground'
                        }`} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-sm">{type.label}</div>
                        <div className="text-xs text-muted-foreground mt-1">{type.description}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Short Summary */}
          <div>
            <label htmlFor="short-summary" className="block text-sm font-medium mb-2">
              Brief description *
            </label>
            <input
              id="short-summary"
              type="text"
              value={shortSummary}
              onChange={(e) => setShortSummary(e.target.value)}
              placeholder="Summarize your feedback in one sentence..."
              className="w-full px-4 py-3 border border-border rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
              maxLength={200}
              required
            />
            <div className="text-xs text-muted-foreground mt-1">
              {shortSummary.length}/200 characters
            </div>
          </div>

          {/* Detailed Feedback */}
          <div>
            <label htmlFor="detailed-feedback" className="block text-sm font-medium mb-2">
              Additional details (optional)
            </label>
            <textarea
              id="detailed-feedback"
              value={detailedFeedback}
              onChange={(e) => setDetailedFeedback(e.target.value)}
              placeholder="Provide more context, steps to reproduce, or additional thoughts..."
              className="w-full px-4 py-3 border border-border rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors resize-none"
              rows={4}
              maxLength={1000}
            />
            <div className="text-xs text-muted-foreground mt-1">
              {detailedFeedback.length}/1000 characters
            </div>
          </div>

          {/* Auto-captured Info */}
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="text-xs text-muted-foreground space-y-1">
              <div><strong>Auto-captured:</strong></div>
              <div>• User: {user?.email}</div>
              <div>• Page: {currentRoute || window.location.pathname}</div>
              <div>• Time: {new Date().toLocaleString()}</div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-border rounded-xl hover:bg-muted transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!feedbackType || !shortSummary.trim() || isSubmitting}
              className="flex-1 px-6 py-3 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
